/* AgentMeeting — Landing page */
import { MarketingBar, MarketingFooter, GoToFn } from '../shared/marketing-bar';
import { useI18n } from '../shared/i18n';

export function Landing({ goTo, signedIn }: { goTo: GoToFn; signedIn?: boolean }) {
  return (
    <div className="mkt-page">
      <MarketingBar here="landing" goTo={goTo} signedIn={signedIn} />
      <Hero goTo={goTo} />
      <SocialProof />
      <DemoSection />
      <FeatureGrid />
      <HowItWorks />
      <ComparisonSection />
      <UseCases />
      <FinalCTA goTo={goTo} />
      <MarketingFooter goTo={goTo} />
    </div>
  );
}

function Hero({ goTo }: { goTo: GoToFn }) {
  const { t, lang } = useI18n();
  return (
    <section className="hero" id="top">
      <div className="ambient">
        <div className="blob" style={{
          width: 480, height: 480, top: -120, left: '55%',
          background: 'radial-gradient(circle, var(--live) 0%, transparent 70%)',
        }} />
        <div className="blob" style={{
          width: 360, height: 360, top: 80, left: '20%',
          background: 'radial-gradient(circle, var(--judge) 0%, transparent 70%)',
        }} />
      </div>

      <div className="mkt-container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="eyebrow">{t('land.eyebrow')}</div>
        <h1>
          {t('land.h1.line1')}<br />
          <span className="accent">{t('land.h1.line2')}</span>
        </h1>
        <p className="lead">{t('land.lead')}</p>
        <div className="cta-row" style={{ marginTop: 28 }}>
          <button className="btn-lg primary" onClick={() => goTo('signup')}>
            {t('land.cta.primary')}
          </button>
          <button className="btn-lg ghost" onClick={() => goTo('app/agenda')}>
            {lang === 'zh' ? '▷ 不注册先试用' : '▷ Try without account'}
          </button>
          <span className="mono tiny dim" style={{ marginLeft: 8 }}>
            {t('land.cta.note')}
          </span>
        </div>

        <div className="flex gap-4 wrap mono" style={{
          marginTop: 56, paddingTop: 28,
          borderTop: '1px solid var(--line-1)',
          fontSize: 11, color: 'var(--fg-2)',
        }}>
          {([
            ['MULTI-LLM',     'Claude · GPT · Gemini · Llama'],
            ['SCHEDULING',    'round-robin · stance-pair · reactive · moderated'],
            ['OBSERVABILITY', 'replay · fork · per-turn context inspector'],
            ['BILLING',       'token-accurate · per-meeting cap · BYOK'],
          ] as const).map(([k, v]) => (
            <div key={k} className="col gap-1" style={{ minWidth: 200 }}>
              <span className="dim">[{k}]</span>
              <span style={{ color: 'var(--fg-1)' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SocialProof() {
  return (
    <section style={{ padding: '40px 0', borderTop: '1px solid var(--line-1)' }}>
      <div className="mkt-container">
        <div className="logo-row">
          <span>trusted by teams at</span>
          {['stripe-ish', 'linear-ish', 'vercel-ish', 'shopify-ish', 'figma-ish', 'notion-ish'].map(n => (
            <span key={n} className="dim" style={{ letterSpacing: '0.04em' }}>{n}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoSection() {
  return (
    <section className="section">
      <div className="mkt-container">
        <div className="section-tag">// live demo</div>
        <h2>看它怎么开一场会。</h2>
        <p className="sub">
          下面是一场真实跑过的决策会议: 6 个 agent 围绕 "Q3 是否进巴西"
          辩论 6 轮、产出会议纪要。点 ⌗ context 可看到任一 agent 发言前接收到的完整 prompt。
        </p>
        <div className="demo-card">
          <DemoTerminalBlock />
        </div>
      </div>
    </section>
  );
}

function DemoTerminalBlock() {
  const lines: { c: string; t: string }[] = [
    { c: 'dim', t: '$ agentmeeting start brazil-launch-q3 --type decision --budget 5.00' },
    { c: 'mono dim', t: '> spinning up 6 agents · claude / gpt / gemini · estimated runtime ~14m' },
    { c: 'mono dim', t: '> agenda loaded · 6 rounds · scheduling=stance-pair' },
    { c: 'sep', t: '─── R1 · 开场陈述 · round-robin ─────────────' },
    { c: 'pro',     t: '[CEO Aria]    我们应该立刻进入巴西。圣保罗中型企业…' },
    { c: 'finance', t: '[CFO Bran]    @Aria 数字上不同意。CAC 翻 1.8x, ARPU -42%…' },
    { c: 'err',     t: '[CRO Cara]    LGPD 合规 + 8 周工程量, 前 18 个月 P&L 难看' },
    { c: 'sep', t: '─── R3 · 修正与回应 · reactive · @-priority ──' },
    { c: 'pro',     t: '[CEO Aria]    回应 @Bran: 改成 9m pilot, $1.2M cap, 不及预期撤' },
    { c: 'finance', t: '[CFO Bran]    可接受。底线: 第 4 月 ARR ≥ $80k, payback < 14m▌' },
    { c: 'sep', t: '─── R5 · 投票 · 阈值 4/6 ───────────────' },
    { c: 'live',    t: '✓ APPROVED 4-1-1 · approve_with_conditions · CFO casts deciding' },
    { c: 'mono dim', t: '> minutes drafted · ~870 tok · $0.27 spent · saved ./minutes.md' },
  ];
  const colorMap: Record<string, string> = {
    pro: 'var(--pro)', err: 'var(--err)', finance: 'var(--finance)',
    live: 'var(--live)', dim: 'var(--fg-3)', 'mono dim': 'var(--fg-2)',
  };
  return (
    <div className="col" style={{ padding: '14px 18px', fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.7 }}>
      <div className="flex center between" style={{ marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--line-1)' }}>
        <div className="flex center gap-2">
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ff5f57' }} />
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#febc2e' }} />
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#28c840' }} />
          <span className="dim mono tiny" style={{ marginLeft: 12 }}>~/meetings/brazil-launch-q3 · live</span>
        </div>
        <div className="flex center gap-3 mono tiny dim">
          <span><span className="dot live pulse" /> running</span>
          <span>r5/6</span>
          <span>$ 0.27 / 5.00</span>
        </div>
      </div>
      {lines.map((l, i) => {
        if (l.c === 'sep') return (
          <div key={i} className="dim" style={{ fontSize: 10, letterSpacing: '0.04em', margin: '8px 0 4px' }}>
            {l.t}
          </div>
        );
        return (
          <div key={i} style={{ color: colorMap[l.c] || 'var(--fg-1)', whiteSpace: 'pre-wrap' }}>
            {l.t}
          </div>
        );
      })}
    </div>
  );
}

function FeatureGrid() {
  const { lang } = useI18n();
  const features = [
    { num: '01', title: '多模型混合编排', body: '同一场会议里, CEO 用 GPT-5、CFO 用 Claude、评委用 Gemini。每个 agent 独立 persona、独立 model、独立 temperature。' },
    { num: '02', title: '调度即是规则', body: 'round-robin / stance-pair / moderated / reactive / free-form。换调度策略, 整场会议的动力学就变。' },
    { num: '03', title: '可回放, 可分叉', body: '任意时间点暂停、倒带、修改某个 agent 的 prompt 后重跑分支, 三条剧情线并排对比。' },
    { num: '04', title: '上下文可观测', body: '点开任一发言, 看到这个 agent 接收到的完整 prompt: system / 知识库 / 摘要 / transcript / 当轮指令。' },
    { num: '05', title: 'Token-accurate billing', body: '每条发言显示 ↑in / ↓out / $cost。设预算上限, 触顶自动暂停。BYOK 模式只收平台费。' },
    { num: '06', title: '人在回路', body: '你可以做参与者(就是一个 agent)、主持人(决定谁说话)、导演(暂停、注入 system note、fork)。' },
  ];
  return (
    <section className="section" id="features">
      <div className="mkt-container">
        <div className="section-tag">// {lang === 'zh' ? '功能' : 'features'}</div>
        <h2>不是把几个 chatbot 拼起来。</h2>
        <p className="sub">
          AutoGen / CrewAI 关心 "agent 之间能不能交换消息";
          AgentMeeting 关心 <span style={{ color: 'var(--fg-0)' }}>"作为一场会议, 它能不能产出可信的决策"</span>。
        </p>
        <div className="feature-grid">
          {features.map(f => (
            <div key={f.num} className="feature-card">
              <span className="num">{f.num}</span>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: '01', cmd: '$ agentmeeting init',    title: '搭班子',  body: '从 starter pack(高管会议 / 红蓝对抗 / 苏格拉底)开始, 或在 Studio 里一个个调 persona、挂知识库。' },
    { n: '02', cmd: '$ agentmeeting agenda',  title: '排议程',  body: '决定开几轮、每轮什么调度策略、给 agent 什么 instruction、什么条件下结束(预算、阈值、无新论点)。' },
    { n: '03', cmd: '$ agentmeeting run',     title: '开会',    body: '回车, agent 开始按议程发言。你可以旁观、参与、随时暂停。LIVE 指示灯告诉你当前哪个模型在生成 token。' },
    { n: '04', cmd: '$ agentmeeting resolve', title: '收口',    body: '到投票轮自动唱票, 评委(如果有)给判词, 系统生成会议纪要 — 决议 / 反对意见 / 行动项 / 引用源。' },
  ];
  return (
    <section className="section" id="how">
      <div className="mkt-container">
        <div className="section-tag">// how it works</div>
        <h2>四步开一场会。</h2>
        <div className="col gap-3" style={{ maxWidth: 880, marginTop: 32 }}>
          {steps.map(s => (
            <div key={s.n} className="flex" style={{
              padding: '24px 0', borderTop: '1px solid var(--line-1)', gap: 32,
            }}>
              <div className="mono dim" style={{ fontSize: 11, width: 60 }}>{s.n}</div>
              <div className="col gap-2" style={{ flex: 1 }}>
                <div className="mono" style={{ color: 'var(--live)', fontSize: 12 }}>{s.cmd}</div>
                <h3 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.01em', fontWeight: 500 }}>{s.title}</h3>
                <p style={{ margin: 0, color: 'var(--fg-1)', fontSize: 14, lineHeight: 1.6 }}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows: [string, boolean, boolean, boolean][] = [
    ['真正的"会议"语义(议程 / 轮次 / 调度策略)',  true, false, false],
    ['多模型混合(同一场会跨厂商)',                 true, false, true],
    ['人在回路 · 主持人 / 导演视角',                  true, false, false],
    ['消息时间轴 + 暂停 / 倒带 / fork',              true, false, false],
    ['每条消息可观测的完整 prompt',                  true, false, false],
    ['投票 / 阈值 / 自动会议纪要',                   true, false, false],
    ['Token-accurate billing + 预算上限',           true, true,  false],
    ['零代码 UI · 不用写 Python',                    true, false, true],
  ];
  return (
    <section className="section">
      <div className="mkt-container">
        <div className="section-tag">// vs.</div>
        <h2>跟 AutoGen / CrewAI / 直接调 API 比。</h2>
        <div style={{ marginTop: 32, border: '1px solid var(--line-2)', borderRadius: 8, overflow: 'hidden' }}>
          <div className="grid mono" style={{
            display: 'grid', gridTemplateColumns: '2.4fr 1fr 1fr 1fr',
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--line-2)',
            fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: 'var(--fg-2)',
          }}>
            <div style={{ padding: '12px 16px' }}>capability</div>
            <div style={{ padding: '12px 16px', textAlign: 'center', color: 'var(--live)' }}>AgentMeeting</div>
            <div style={{ padding: '12px 16px', textAlign: 'center' }}>AutoGen / CrewAI</div>
            <div style={{ padding: '12px 16px', textAlign: 'center' }}>raw LLM API</div>
          </div>
          {rows.map(([label, am, autogen, raw], i) => (
            <div key={i} className="grid" style={{
              display: 'grid', gridTemplateColumns: '2.4fr 1fr 1fr 1fr',
              borderBottom: i < rows.length - 1 ? '1px solid var(--line-1)' : 'none',
              background: 'var(--bg-1)',
              fontSize: 13,
            }}>
              <div style={{ padding: '14px 16px' }}>{label}</div>
              <CmpCell on={am} accent />
              <CmpCell on={autogen} />
              <CmpCell on={raw} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CmpCell({ on, accent }: { on: boolean; accent?: boolean }) {
  return (
    <div style={{
      padding: '14px 16px', textAlign: 'center',
      fontFamily: 'var(--mono)', fontSize: 14,
      color: on ? (accent ? 'var(--live)' : 'var(--fg-1)') : 'var(--fg-3)',
    }}>{on ? '✓' : '—'}</div>
  );
}

function UseCases() {
  const cases = [
    { tag: 'startup',    title: '产品评审会',   body: 'CEO / CTO / 设计师 / 用户代表围一桌, 讨论某个 feature 砍不砍。' },
    { tag: 'security',   title: '红蓝对抗',     body: '攻方持续找漏洞, 守方反驳, 评委判定胜负 + 给修复优先级。' },
    { tag: 'edu',        title: '苏格拉底课堂', body: '导师不断追问学生的论点, 学生反思 — 适合做辩论训练。' },
    { tag: 'research',   title: '多专家会诊',   body: '医学影像 + 法务 + 患者代表 + AI 质控,  一起评估同一份病例。' },
    { tag: 'creative',   title: '剧本会',       body: '编剧 / 导演 / 制片 / 观众视角各 1 个 agent, 一起改一个开头。' },
    { tag: 'governance', title: 'DAO 提案',     body: '把治理论坛塞进圆桌, 每方都有正式的 1 轮陈述 + 1 轮质询 + 投票。' },
  ];
  return (
    <section className="section" id="cases">
      <div className="mkt-container">
        <div className="section-tag">// who's using it</div>
        <h2>不止是技术演示。</h2>
        <div style={{
          marginTop: 24,
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
        }}>
          {cases.map(c => (
            <div key={c.tag} style={{
              border: '1px solid var(--line-1)', borderRadius: 6,
              padding: 22, background: 'var(--bg-1)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <span className="tag mono tiny" style={{ alignSelf: 'flex-start' }}>{c.tag}</span>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 500 }}>{c.title}</h3>
              <p style={{ margin: 0, color: 'var(--fg-1)', fontSize: 13, lineHeight: 1.55 }}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ goTo }: { goTo: GoToFn }) {
  return (
    <section className="section" style={{ paddingTop: 96, paddingBottom: 96 }}>
      <div className="mkt-container" style={{ textAlign: 'center' }}>
        <div className="section-tag" style={{ display: 'inline-block' }}>// ready?</div>
        <h2 style={{
          fontSize: 'clamp(32px, 4vw, 56px)',
          letterSpacing: '-0.025em',
          margin: '14px auto 18px', maxWidth: 800,
        }}>
          你下一个艰难的决定, <br />
          让六个 AI 替你吵一吵。
        </h2>
        <p className="sub" style={{ margin: '0 auto 32px', textAlign: 'center' }}>
          注册得 $5 试用额度 · 跑完 ~3 场决策会议 · 满意了再付钱。
        </p>
        <div className="cta-row" style={{ justifyContent: 'center' }}>
          <button className="btn-lg primary" onClick={() => goTo('signup')}>
            $ start free →
          </button>
          <button className="btn-lg ghost" onClick={() => goTo('pricing')}>
            see pricing
          </button>
        </div>
      </div>
    </section>
  );
}
